/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import FetchStatus from 'src/types/FetchStatus';
import { OpPost, GetPostResponse } from 'src/types/DiscussionPost';
import { customFetch, getElementsMetadata, serializeToHtml } from 'src/utils';
import { THREAD_API, DRAFT_API, getPostApi } from 'src/constants/apiEndpoints';
import { Descendant } from 'slate';
import { NONE_OPTION } from 'src/utils/formValidations';
import type { AppState } from '../store';
import { selectMarketplaceId } from './userInfo';

export interface DraftsState {
  drafts: { [key: string]: OpPost },
  orderedDraftIds: Array<string>,
  editableThread?: OpPost,
  listStatus: FetchStatus,
  editableThreadStatus: FetchStatus,
  saveStatus: FetchStatus,
  deleteStatus: FetchStatus,
  error?: string
}

export const initialState: DraftsState = {
  drafts: {},
  orderedDraftIds: [],
  listStatus: FetchStatus.idle,
  editableThreadStatus: FetchStatus.idle,
  saveStatus: FetchStatus.idle,
  deleteStatus: FetchStatus.idle,
};

export const fetchDraftList = createAsyncThunk(
  'drafts/fetchDraftList',
  async (noop, { getState, rejectWithValue }) => customFetch(
    DRAFT_API,
    {
      method: 'POST',
      body: JSON.stringify({
        marketplaceId: selectMarketplaceId(getState() as AppState),
      }),
    },
    (error: Error) => rejectWithValue(error.message),
  ),
);

export const fetchEditableThread = createAsyncThunk(
  'drafts/fetchEditableThread',
  async (
    threadId: string,
    { rejectWithValue, getState },
  ) => await customFetch(
    getPostApi(threadId, threadId),
    {
      method: 'POST',
      body: JSON.stringify({
        marketplaceId: selectMarketplaceId(getState() as AppState),
      }),
    },
    (error: Error) => rejectWithValue(error.message),
  ) as GetPostResponse,
);

export const saveDraft = createAsyncThunk(
  'drafts/saveDraft',
  async (values: {
    threadId?: string;
    groupId: string;
    title: string;
    content: Descendant[];
    categoryId: string;
    tags: Array<string>;
  }, { rejectWithValue, getState }) => {
    const {
      threadId, groupId, title, content, categoryId, tags,
    } = values;
    return customFetch(
      `${THREAD_API}${threadId ? `/${threadId}` : ''}`,
      {
        method: threadId ? 'PUT' : 'POST',
        body: JSON.stringify({
          ...(groupId === NONE_OPTION ? {} : { groupId }),
          title,
          content: serializeToHtml(content),
          categoryId,
          tags,
          marketplaceId: selectMarketplaceId(getState() as AppState),
          contentType: 'FORUMS',
          draft: true,
          contentMetadata: getElementsMetadata(content),
        }),
      },
      (error: Error) => rejectWithValue(error.message),
    );
  },
);

export const deleteDraft = createAsyncThunk(
  'drafts/deleteDraft',
  async (draftId: string, { rejectWithValue }) => customFetch(
    `${THREAD_API}/${draftId}/post/${draftId}`,
    {
      method: 'DELETE',
      body: JSON.stringify({}),
    },
    (error: Error) => rejectWithValue(error.message),
  ),
);

export const drafts = createSlice({
  name: 'drafts',
  initialState,
  reducers: {
    reset: () => initialState,
    resetEditableThread: (state) => {
      state.editableThread = initialState.editableThread;
      state.editableThreadStatus = initialState.editableThreadStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchDraftList
      .addCase(fetchDraftList.rejected, (state: DraftsState, action) => {
        state.error = action.payload as string;
        state.listStatus = FetchStatus.failed;
      })
      .addCase(fetchDraftList.pending, (state: DraftsState) => {
        state.listStatus = FetchStatus.loading;
      })
      .addCase(fetchDraftList.fulfilled, (state: DraftsState, action) => {
        (action.payload.drafts as Array<OpPost>).sort(
          (draftA, draftB) => {
            const timeA = draftA.updatedAt || draftA.createdAt;
            const timeB = draftB.updatedAt || draftB.createdAt;
            return timeA > timeB ? -1 : 1;
          },
        );
        state.orderedDraftIds = action.payload.drafts.map((draft: OpPost) => draft.threadId);
        action.payload.drafts.forEach((draft: OpPost) => {
          state.drafts[draft.threadId] = draft;
        });
        state.listStatus = FetchStatus.complete;
      })
      // fetchEditableThread
      .addCase(fetchEditableThread.rejected, (state: DraftsState, action) => {
        state.error = action.payload as string;
        state.editableThreadStatus = FetchStatus.failed;
      })
      .addCase(fetchEditableThread.pending, (state: DraftsState) => {
        state.editableThreadStatus = FetchStatus.loading;
        state.editableThread = undefined;
      })
      .addCase(fetchEditableThread.fulfilled, (state: DraftsState, action) => {
        state.editableThread = action.payload.post as OpPost;
        state.saveStatus = FetchStatus.idle;
        state.deleteStatus = FetchStatus.idle;
        state.editableThreadStatus = FetchStatus.complete;
      })
      // saveDraft
      .addCase(saveDraft.rejected, (state: DraftsState, action) => {
        state.error = action.payload as string;
        state.saveStatus = FetchStatus.failed;
      })
      .addCase(saveDraft.pending, (state: DraftsState) => {
        state.saveStatus = FetchStatus.loading;
      })
      .addCase(saveDraft.fulfilled, (state: DraftsState, action) => {
        const draft = action.payload.post;
        const draftId = draft.threadId;
        state.drafts[draftId] = draft;
        if (!state.orderedDraftIds.includes(draftId)) state.orderedDraftIds.unshift(draftId);
        state.orderedDraftIds = state.orderedDraftIds.filter((id) => id !== draftId);
        state.orderedDraftIds.unshift(draftId);
        state.editableThread = draft;
        state.saveStatus = FetchStatus.complete;
        state.editableThreadStatus = FetchStatus.complete;
      })
      // deleteDraft
      .addCase(deleteDraft.rejected, (state: DraftsState, action) => {
        state.error = action.payload as string;
        state.deleteStatus = FetchStatus.failed;
      })
      .addCase(deleteDraft.pending, (state: DraftsState) => {
        state.deleteStatus = FetchStatus.loading;
      })
      .addCase(deleteDraft.fulfilled, (state: DraftsState, action) => {
        const draftId = action.meta.arg;
        const index = state.orderedDraftIds.findIndex(
          (id) => id === draftId,
        );
        if (index !== -1) state.orderedDraftIds.splice(index, 1);
        if (state.editableThread?.threadId === draftId) {
          delete state.editableThread;
          state.saveStatus = FetchStatus.idle;
        }
        delete state.drafts[draftId];
        state.deleteStatus = FetchStatus.complete;
      });
  },
});

export const { reset, resetEditableThread } = drafts.actions;

export default drafts.reducer;
